import { useState, useEffect, useCallback } from "react";
import "@/App.css";

// Assets - Brand System v3.0
const LOGO_URL = "https://customer-assets.emergentagent.com/job_leader-toolkit-2/artifacts/2yh2i1ch_Ember-Logo-1024-transparent.png";
const HEADSHOT_URL = "https://customer-assets.emergentagent.com/job_leader-toolkit-2/artifacts/allt22et_Matthew_Callison_Headshot.jpg";
const CALENDLY_URL = "https://calendly.com/matt-emberimpact/30-minute-discovery-call";
const KIT_FORM_ACTION = "https://app.kit.com/forms/9309943/subscriptions";

// Security: Basic input sanitization
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .trim()
    .slice(0, 200); // Limit length
};

// Email validation
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Arrow Right SVG Component
const ArrowRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="arrow-icon">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      className={`nav-fixed ${scrolled ? 'nav-scrolled' : ''}`} 
      data-testid="main-navigation"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="nav-container">
        <a href="/" className="nav-logo" data-testid="nav-logo" aria-label="Ember - Go to homepage">
          <img src={LOGO_URL} alt="" className="nav-logo-img" aria-hidden="true" />
          <span className="nav-logo-text">EMBER</span>
        </a>
        <a href="#join" className="nav-cta-btn" data-testid="nav-join-btn">
          Join the Waitlist
        </a>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="hero-section" data-testid="hero-section" aria-labelledby="hero-heading">
    <div className="hero-content">
      <p className="hero-eyebrow fade-up" style={{ animationDelay: '0.2s' }} aria-label="Program name">
        THE CRUCIBLE — A PEER LEADERSHIP ACCELERATOR
      </p>
      <h1 id="hero-heading" className="hero-h1 fade-up" style={{ animationDelay: '0.35s' }}>
        In 8 weeks, you'll stop carrying your hardest challenge <em className="gradient-text">alone.</em>
      </h1>
      <p className="hero-subhead fade-up" style={{ animationDelay: '0.5s' }}>
        You already know what's keeping you up at night. You just don't have anyone who can hear it without an agenda.
      </p>
      <a 
        href="#join" 
        className="hero-cta fade-up" 
        style={{ animationDelay: '0.65s' }} 
        data-testid="hero-cta-btn"
        aria-label="Join the Founding Cohort Waitlist - scroll to signup form"
      >
        Join the Founding Cohort Waitlist <ArrowRight />
      </a>
    </div>
  </section>
);

// This Is For You If Section
const ForYouSection = () => (
  <section className="for-you-section" data-testid="for-you-section" aria-labelledby="foryou-heading">
    <div className="gradient-bar" aria-hidden="true"></div>
    <div className="section-container">
      <p className="section-label">THIS IS FOR YOU IF</p>
      <h2 id="foryou-heading" className="section-heading">You're nodding along to at least three of these.</h2>
      <ul className="checklist" role="list" aria-label="Signs this program is for you">
        <li className="checklist-item">
          <span className="check-icon" aria-hidden="true">✓</span>
          <span>You're sitting on a decision about someone's role, a reorganization, or a difficult conversation — and the only people you can talk to about it have a stake in the outcome.</span>
        </li>
        <li className="checklist-item">
          <span className="check-icon" aria-hidden="true">✓</span>
          <span>You've done the leadership books, the podcasts, the conferences. You're not short on knowledge. You're short on peers who challenge you.</span>
        </li>
        <li className="checklist-item">
          <span className="check-icon" aria-hidden="true">✓</span>
          <span>You keep circling the same challenge — a team dynamic, a career crossroads, a strategy question — and you need a perspective your organization can't give you.</span>
        </li>
        <li className="checklist-item">
          <span className="check-icon" aria-hidden="true">✓</span>
          <span>You're willing to be honest about what's not working — and to hear the same from people with no reason to sugarcoat it.</span>
        </li>
        <li className="checklist-item">
          <span className="check-icon" aria-hidden="true">✓</span>
          <span>You believe leadership is about influence and positive impact — not title or org chart position.</span>
        </li>
      </ul>
      <div className="inline-cta-bar" role="complementary" aria-label="Call to action">
        <span className="inline-cta-text">Sound like you?</span>
        <span className="inline-cta-urgency">Limited to the first 50 on the waitlist.</span>
        <a href="#join" className="inline-cta-btn" data-testid="for-you-join-btn">Join the Waitlist</a>
      </div>
    </div>
  </section>
);

// Origin Story Section
const OriginSection = () => (
  <section className="origin-section" data-testid="origin-section">
    <h2 className="visually-hidden">Why I Built The Crucible</h2>
    <div className="section-container">
      <figure className="origin-quote">
        <blockquote>
          <p>I built The Crucible because I know what it's like to sit with a decision you can't talk about honestly — not with your boss, not with your team, not with the people closest to the outcome. I'd been facilitating peer groups for years and watched what happened when leaders finally had a room where nobody needed them to perform, and nobody had an agenda. The conversations went to a place that coaching, conferences, and books never reached. I wanted to build that room and open the door.</p>
        </blockquote>
        <figcaption>— Matt Callison, PhD</figcaption>
      </figure>
    </div>
  </section>
);

// How It Works Section
const HowItWorksSection = () => (
  <section className="how-it-works-section" data-testid="how-it-works-section">
    <div className="gradient-bar"></div>
    <div className="section-container">
      <p className="section-label">HOW IT WORKS</p>
      <h2 className="section-heading">A structured container — not a course.</h2>
      <p className="section-description">
        Every week for 8 weeks, you meet virtually with 8–12 leaders from different sectors. Each session follows a deliberate structure that builds trust first, then uses that trust to do real work on real challenges.
      </p>
      
      <div className="not-box">
        <p className="not-box-title">This is not:</p>
        <ul className="not-list">
          <li>✕ A mastermind where one person talks and everyone nods</li>
          <li>✕ Coaching where someone tells you what to do</li>
          <li>✕ A networking group that trades business cards and calls it connection</li>
          <li>✕ A course with slides, modules, and homework</li>
        </ul>
      </div>
      
      <div className="features-grid">
        <div className="feature-card">
          <span className="feature-icon">🔥</span>
          <h3>Cross-Sector by Design</h3>
          <p>Leaders from different industries — education, business, nonprofit, government. No competitors. No politics. Just honest perspective.</p>
        </div>
        <div className="feature-card">
          <span className="feature-icon">🛡️</span>
          <h3>Psychological Safety First</h3>
          <p>Trust isn't hoped for — it's built deliberately through session structure, facilitator skill, and group agreements before any vulnerability is asked.</p>
        </div>
        <div className="feature-card">
          <span className="feature-icon">📋</span>
          <h3>Deep Preparation</h3>
          <p>Presenters complete a comprehensive worksheet shared in advance. The group arrives warm — ready for insight, not surface-level Q&A.</p>
        </div>
        <div className="feature-card">
          <span className="feature-icon">🔨</span>
          <h3>The FORGE Protocol™</h3>
          <p>A proprietary structured conversation method. Five phases that move from clarifying the real problem to concrete commitments with peer accountability.</p>
        </div>
      </div>
    </div>
  </section>
);

// FORGE Protocol Section
const ForgeSection = () => (
  <section className="forge-section" data-testid="forge-section">
    <div className="section-container">
      <p className="section-label glow">THE FORGE PROTOCOL™</p>
      <h2 className="section-heading light">Every session, one leader brings a real challenge. The group transforms it.</h2>
      <p className="section-description light">
        Each conversation follows five phases designed to move past surface advice into the kind of insight you can't access alone.
      </p>
      
      <ol className="forge-steps">
        <li className="forge-step">
          <span className="forge-letter">F</span>
          <div className="forge-content">
            <h3>Focus</h3>
            <p>Clarifying questions that cut through noise to the real challenge underneath.</p>
          </div>
        </li>
        <li className="forge-step">
          <span className="forge-letter">O</span>
          <div className="forge-content">
            <h3>Observe</h3>
            <p>Deeper probing that surfaces hidden assumptions and patterns the presenter can't see.</p>
          </div>
        </li>
        <li className="forge-step">
          <span className="forge-letter">R</span>
          <div className="forge-content">
            <h3>Reflect</h3>
            <p>Honest feedback — strengths acknowledged, blind spots named, new perspectives offered.</p>
          </div>
        </li>
        <li className="forge-step">
          <span className="forge-letter">G</span>
          <div className="forge-content">
            <h3>Grow</h3>
            <p>Creative options from outside your industry — including the option to do nothing.</p>
          </div>
        </li>
        <li className="forge-step">
          <span className="forge-letter">E</span>
          <div className="forge-content">
            <h3>Execute</h3>
            <p>Concrete commitments with peer accountability. Insight without action is entertainment.</p>
          </div>
        </li>
      </ol>
      
      <div className="inline-cta-bar dark">
        <span className="inline-cta-text">Bring your hardest challenge. Leave with clarity.</span>
        <span className="inline-cta-urgency">Limited to the first 50 on the waitlist.</span>
        <a href="#join" className="inline-cta-btn" data-testid="forge-join-btn">Join the Waitlist</a>
      </div>
    </div>
  </section>
);

// Social Proof Section
const SocialProofSection = () => (
  <section className="social-proof-section" data-testid="social-proof-section">
    <div className="gradient-bar"></div>
    <div className="section-container">
      <p className="section-label">WHAT PEOPLE SAY</p>
      <h2 className="section-heading">From leaders who've experienced Matt's facilitation.</h2>
      
      <div className="testimonials-grid">
        <figure className="testimonial-card">
          <blockquote>
            <p>"Working with Matt has been a transformative experience due to his exceptional group facilitation skills. His ability to create an inclusive environment encourages open dialogue, allowing all voices to be heard."</p>
          </blockquote>
          <figcaption>— Jeff Dorn</figcaption>
        </figure>
        <figure className="testimonial-card">
          <blockquote>
            <p>"Matt's leadership in groups I've attended is both commanding and humble. His sincerity draws me in, and his ability to facilitate openings for authentic conversation is skillful. I always feel safe, seen, and heard."</p>
          </blockquote>
          <figcaption>— Mark Caddell</figcaption>
        </figure>
      </div>
    </div>
  </section>
);

// Facilitator Section
const FacilitatorSection = () => (
  <section className="facilitator-section" data-testid="facilitator-section" aria-labelledby="facilitator-heading">
    <div className="section-container">
      <p className="section-label glow">YOUR FACILITATOR</p>
      
      <div className="facilitator-layout">
        <div className="facilitator-bio">
          <img 
            src={HEADSHOT_URL} 
            alt="Matt Callison, PhD - Facilitator of The Crucible" 
            className="facilitator-headshot"
            loading="lazy"
            width="140"
            height="140"
          />
          <h2 id="facilitator-heading" className="facilitator-name">Matt Callison, PhD</h2>
          <p className="facilitator-text">
            Matt doesn't position himself as a guru. After 4+ years facilitating peer leadership groups — and two decades in education leadership and innovation — he designed The Crucible as a container where the group does the transforming, not the facilitator.
          </p>
        </div>
        
        <div className="credentials-grid" role="list" aria-label="Matt's credentials">
          <div className="credential-card" role="listitem">
            <span className="credential-number">PhD</span>
            <span className="credential-text">Indiana University<br />Bloomington</span>
          </div>
          <div className="credential-card" role="listitem">
            <span className="credential-number">4+ Years</span>
            <span className="credential-text">Peer Group<br />Facilitation</span>
          </div>
          <div className="credential-card" role="listitem">
            <span className="credential-number">2025</span>
            <span className="credential-text">PETE&C Technology<br />Administrator of the Year</span>
          </div>
          <div className="credential-card" role="listitem">
            <span className="credential-number">20+</span>
            <span className="credential-text">Years in Leadership<br />& Innovation</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Lead Capture Section
const LeadCaptureSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ firstName: '', email: '' });

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    const sanitizedName = sanitizeInput(formData.firstName);
    if (!sanitizedName || sanitizedName.length < 1) {
      newErrors.firstName = 'Please enter your first name';
    }
    
    const sanitizedEmail = sanitizeInput(formData.email);
    if (!sanitizedEmail) {
      newErrors.email = 'Please enter your email address';
    } else if (!isValidEmail(sanitizedEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name === 'fields[first_name]' ? 'firstName' : 'email']: value
    }));
    // Clear error when user starts typing
    if (errors[name === 'fields[first_name]' ? 'firstName' : 'email']) {
      setErrors(prev => ({
        ...prev,
        [name === 'fields[first_name]' ? 'firstName' : 'email']: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    const form = e.target;
    const submitData = new FormData(form);
    
    try {
      await fetch(KIT_FORM_ACTION, {
        method: 'POST',
        body: submitData,
        mode: 'no-cors'
      });
      setSubmitted(true);
      // Announce success to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'visually-hidden';
      announcement.textContent = 'Success! You have been added to the waitlist.';
      document.body.appendChild(announcement);
      setTimeout(() => announcement.remove(), 1000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitted(true);
    }
    
    setIsSubmitting(false);
  };

  return (
    <section 
      className="lead-capture-section" 
      id="join" 
      data-testid="lead-capture-section"
      aria-labelledby="join-heading"
    >
      <div className="gradient-bar" aria-hidden="true"></div>
      <div className="section-container">
        <p className="section-label">FOUNDING COHORT</p>
        <h2 id="join-heading" className="section-heading">The founding cohort is forming.<br />Get in early.</h2>
        <p className="section-description">
          Founding cohort members get pilot pricing significantly below future cohorts, plus a direct conversation with Matt before committing.
        </p>
        <p className="urgency-line" role="status" aria-live="polite">Waitlist limited to the first 50.</p>
        
        {!submitted ? (
          <form 
            onSubmit={handleSubmit}
            className="waitlist-form"
            data-testid="waitlist-form"
            noValidate
            aria-describedby="form-description"
          >
            <span id="form-description" className="visually-hidden">
              Join the founding cohort waitlist by entering your name and email
            </span>
            <input type="hidden" name="form_id" value="9309943" />
            <input type="hidden" name="utf8" value="✓" />
            
            <div className="form-fields">
              <div className="form-field-wrapper">
                <label htmlFor="first_name" className="visually-hidden">First Name (required)</label>
                <input
                  type="text"
                  id="first_name"
                  name="fields[first_name]"
                  placeholder="First Name"
                  required
                  className={`form-input ${errors.firstName ? 'form-input-error' : ''}`}
                  data-testid="input-first-name"
                  aria-required="true"
                  aria-invalid={errors.firstName ? 'true' : 'false'}
                  aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                  autoComplete="given-name"
                  onChange={handleInputChange}
                  maxLength={100}
                />
                {errors.firstName && (
                  <span id="firstName-error" className="form-error" role="alert">
                    {errors.firstName}
                  </span>
                )}
              </div>
              
              <div className="form-field-wrapper">
                <label htmlFor="email" className="visually-hidden">Email Address (required)</label>
                <input
                  type="email"
                  id="email"
                  name="email_address"
                  placeholder="Email"
                  required
                  className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                  data-testid="input-email"
                  aria-required="true"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  autoComplete="email"
                  onChange={handleInputChange}
                  maxLength={254}
                />
                {errors.email && (
                  <span id="email-error" className="form-error" role="alert">
                    {errors.email}
                  </span>
                )}
              </div>
              
              <button 
                type="submit" 
                className="form-submit"
                disabled={isSubmitting}
                data-testid="submit-btn"
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Joining...' : 'Join the Waitlist'}
              </button>
            </div>
            <p className="form-note">No spam. No sales pitch. Just first access when we launch.</p>
          </form>
        ) : (
          <div 
            className="success-state" 
            role="alert" 
            aria-live="assertive"
            data-testid="success-message"
          >
            <h3>You're on the list.</h3>
            <p>Want to find out now if The Crucible is the right fit? Book a 30-minute conversation with Matt — no pitch, just a real discussion about your leadership challenges.</p>
            <a 
              href={CALENDLY_URL} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="calendly-btn"
              data-testid="calendly-btn"
              aria-label="Book a 30-minute conversation with Matt (opens in new tab)"
            >
              Book a 30-Minute Conversation
              <span className="visually-hidden"> (opens in new tab)</span>
            </a>
            <p className="fallback-text">Or just wait — we'll email you when applications open.</p>
          </div>
        )}
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => (
  <footer className="footer" data-testid="footer" role="contentinfo">
    <div className="footer-container">
      <div className="footer-logo">
        <img src={LOGO_URL} alt="" className="footer-logo-img" aria-hidden="true" loading="lazy" />
        <span className="footer-logo-text">EMBER</span>
      </div>
      <p className="footer-copyright">© 2025 Ember Impact Ventures LLC. All rights reserved.</p>
      <a 
        href="mailto:matt@emberleaders.com" 
        className="footer-email"
        aria-label="Email Matt at matt@emberleaders.com"
      >
        matt@emberleaders.com
      </a>
    </div>
  </footer>
);

function App() {
  return (
    <div className="ember-app">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Navigation />
      <main id="main-content">
        <HeroSection />
        <ForYouSection />
        <OriginSection />
        <HowItWorksSection />
        <ForgeSection />
        <SocialProofSection />
        <FacilitatorSection />
        <LeadCaptureSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
